'use client';

import Link from 'next/link';
import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import type { RouteEntry } from '@/config/routes';
import { Icon } from './icons';

type NavGroup = { label: string; items: readonly RouteEntry[] };
const focusable = 'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/** The mobile navigation is a modal overlay, so focus is contained until it closes. */
export function MobileMenu({ groups }: { groups: readonly NavGroup[] }) {
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const canUseDOM = useSyncExternalStore(() => () => undefined, () => true, () => false);

  const close = () => {
    setOpen(false);
    setOpenGroup(null);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const previousOverflow = document.body.style.overflow;
    const background = [...document.querySelectorAll<HTMLElement>('body > header, body > main, body > footer, body > #announcements')];
    document.body.style.overflow = 'hidden';
    background.forEach(element => { element.inert = true; });
    panel?.querySelector<HTMLElement>(focusable)?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key !== 'Tab' || !panel) return;
      const items = [...panel.querySelectorAll<HTMLElement>(focusable)];
      if (!items.length) return;
      const first = items[0]; const last = items.at(-1)!;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      background.forEach(element => { element.inert = false; });
    };
  }, [open]);

  return <div className="mobile-menu-disclosure">
    <button ref={triggerRef} className="menu-button" type="button" aria-expanded={open} aria-controls="mobile-navigation" onClick={() => setOpen(value => !value)}>
      <Icon name="menu" /><span className="sr-only">بازکردن منوی اصلی</span>
    </button>
    {open && canUseDOM ? createPortal(<div className="mobile-menu-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) close(); }}>
      <div ref={panelRef} className="mobile-menu" id="mobile-navigation" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="mobile-menu__topbar"><strong id={titleId}>منوی اصلی</strong><button type="button" className="mobile-menu__close" onClick={close}><span aria-hidden>×</span><span className="sr-only">بستن منوی اصلی</span></button></div>
        <nav aria-label="ناوبری اصلی موبایل">
          {groups.map((group, index) => {
            const groupId = `mobile-menu-group-${index}`;
            const expanded = openGroup === groupId;
            return <div className={`mobile-menu__group${expanded ? ' mobile-menu__group--open' : ''}`} key={group.label}>
              <button type="button" aria-expanded={expanded} aria-controls={groupId} onClick={() => setOpenGroup(expanded ? null : groupId)}>
                <span>{group.label}</span><span className="mobile-menu__chevron" aria-hidden>⌄</span>
              </button>
              <div className="mobile-menu__links" id={groupId} hidden={!expanded}>{group.items.map(item => <Link key={item.id} href={item.href} onClick={close}>{item.label}</Link>)}</div>
            </div>;
          })}
          <Link href="/blog/" onClick={close}>وبلاگ</Link><Link href="/about/" onClick={close}>درباره ما</Link>
          <Link className="button button--primary" href="/demo/" onClick={close}>درخواست دمو</Link>
        </nav>
      </div>
    </div>, document.body) : null}
  </div>;
}
